import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import cn from 'clsx';
import { uniqBy } from 'lodash';
import styles from './email-actions-dialog.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { MultiSelectEmailController, Person } from '@components-clean/core/multi-select-email/multi-select-email';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBox } from '@components-clean/core/text-box';
import {
    ExtendedCompleteRiskFields,
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import Observation from '@pages-clean/risk-assessment/risk-log/dialogs/email-actions/observation';
import { useLocalState, useTranslation } from '@hooks/index';
import { showMaxLength } from '@utils/filter';
import { ShowError } from '@utils/notification';
import {
    sendRiskMonitoringEmail,
    sendRiskMonitoringEmailVariables,
} from '@queries/generated/legacy-risk-assessment.generated';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { SEND_RISK_MONITORING_EMAIL } from '@queries/legacy-risk-assessment';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface PreviewProps {
    actions: ExtendedRiskActionFields[];
    observations: ExtendedRiskObservationFields[];
    risks: ExtendedCompleteRiskFields[];
    studyRef: string;
    onClose: (message: string) => void;
    canEdit?: boolean;
}

interface EmailActions extends ExtendedRiskActionFields {
    observation: ExtendedRiskObservationFields | undefined;
    risk?: ExtendedCompleteRiskFields;
}

const EmailActionsDialog = ({ risks, observations, actions, studyRef, onClose, canEdit }: PreviewProps) => {
    const { state } = useLocalState();
    const [sendEmail] = useMutation<sendRiskMonitoringEmail, sendRiskMonitoringEmailVariables>(
        SEND_RISK_MONITORING_EMAIL,
    );
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.emailActions' });
    const { t: tCommon } = useTranslation('common');
    const disabledSendButton = useBoolean(false);

    const ownerEmail = actions[0]?.owner?.email;
    let actionsForEmail: EmailActions[] = [];

    actionsForEmail = actions.map((action) => ({
        ...action,
        observation: observations.find(
            (observation: ExtendedRiskObservationFields) => observation.id === action.observationId,
        ),
        risk: risks?.find((risk) => risk?.id === action.riskId),
    }));

    const mostRecent: SortDescriptor = { field: 'created', dir: 'desc' };

    const uniqueRisks = orderBy(
        uniqBy(
            actionsForEmail.map((action) => action.risk),
            'id',
        ),
        [mostRecent],
    );

    const observationOrdering: SortDescriptor[] = [mostRecent];
    const uniqueObservations = orderBy(
        uniqBy(
            actionsForEmail.map((action) => action.observation),
            'id',
        ),
        observationOrdering,
    );

    const risksForEmail =
        uniqueRisks.map((risk) => {
            const currentObservations = uniqueObservations
                .filter((ob) => ob !== undefined)
                .filter((ob) => ob.riskId === risk?.id)
                .map((ob) => ({
                    ...ob,
                    createdBy: ob?.createdBy as unknown as string,
                    updatedBy: ob?.updatedBy as unknown as string,
                    _currentActions: orderBy(
                        actions.filter((action) => action.observationId === ob.id),
                        [mostRecent],
                    ),
                }));

            return {
                ...risk,
                _currentObservations: currentObservations,
            };
        }) ?? [];

    const [studyMembers, setStudyMembers] = useState<Person[]>([]);

    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter(
                    (user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember =>
                        !!user &&
                        user.active === true &&
                        !!user.email &&
                        !!user.firstName &&
                        !!user.lastName &&
                        user.email !== ownerEmail,
                )
                .map((user) => ({
                    firstName: user.firstName!,
                    lastName: user.lastName!,
                    email: user.email!,
                }));
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const {
        control,
        formState: { isValid },
        getValues,
        handleSubmit,
    } = useForm<{ cc: string; notes: string }>({
        mode: 'all',
        defaultValues: {
            cc: '',
            notes: '',
        },
    });

    const onSubmit = async () => {
        disabledSendButton.setTrue();
        const orderedActions = orderBy(actionsForEmail, [mostRecent]);
        sendEmail({
            variables: {
                cc: getValues('cc'),
                notes: getValues('notes'),
                observationIds: orderedActions.map((action) => action?.observation?.id),
                actionIds: orderedActions.map((action) => action.id),
            },
        })
            .then(() => {
                disabledSendButton.setFalse();
                onClose('success');
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
                disabledSendButton.setFalse();
                onClose('error');
            });
    };

    const body = (
        <div className="k-h-full k-w-full">
            <div className="k-w-full k-d-flex-col k-justify-content-start k-align-items-start">
                <Card
                    data-testid="actions-email-preview-details"
                    title={t('actionAlert', { studyNumber: state?.selectedStudy?.studyNumber })}
                    className="k-w-full"
                >
                    <div className="k-w-full" style={{ height: 327 }}>
                        <form id="email-actions">
                            <div className="k-mb-2">
                                <TextBox
                                    label={t('to')}
                                    data-testid="email-actions-owner-email"
                                    readOnly
                                    value={ownerEmail || undefined}
                                />
                            </div>
                            <div className="k-mb-2">
                                <MultiSelectEmailController
                                    data-testid="email-actions-cc-emails"
                                    label={t('cc')}
                                    control={control}
                                    name="cc"
                                    data={studyMembers}
                                    loading={loadingStudyMembers}
                                    placeholder={t('ccHint')}
                                />
                            </div>
                            <div>
                                <TextAreaController
                                    className={styles.notesTextArea}
                                    data-testid="email-actions-notes"
                                    maxLength={4000}
                                    label={t('notes')}
                                    control={control}
                                    name="notes"
                                    placeholder={t('notesHint')}
                                    validationTooltipPosition="auto"
                                />
                            </div>
                        </form>
                    </div>
                </Card>

                {risksForEmail.length
                    ? risksForEmail.map((risk, riskIndex: number) => {
                          const isLastRisk = riskIndex === risksForEmail.length - 1;
                          const isFirstItem = riskIndex === 0;
                          return (
                              <>
                                  <Card
                                      data-testid={`email-actions-risk-${risk.recordId}`}
                                      title={`${t('sectionHeadings.risk')} - ${risk.recordId}`}
                                      className={cn(isFirstItem ? 'k-mt-6' : 'k-mt-3.5', styles.cardCustomBorder)}
                                      bodyStyles={{ padding: 0 }}
                                  >
                                      <div className="k-py-3 k-px-7.5">
                                          <div key={risk.id}>
                                              {risk.recordId || risk.statement ? (
                                                  <div
                                                      className={`content ${
                                                          isLastRisk &&
                                                          risk._currentObservations.length === 0 &&
                                                          'last-element'
                                                      }`}
                                                  >
                                                      <div className="k-font-size-md">
                                                          <p className="k-font-weight-medium k-font-size-lg">
                                                              {t('statement')}
                                                          </p>
                                                          <p>{showMaxLength(risk.statement, 600)}</p>
                                                      </div>
                                                  </div>
                                              ) : null}
                                          </div>
                                          {risk._currentObservations?.length
                                              ? risk._currentObservations.map((observation, obIndex: number) => {
                                                    return <Observation observation={observation} obIndex={obIndex} />;
                                                })
                                              : undefined}
                                      </div>
                                  </Card>
                                  {!isLastRisk && <hr className={styles.hrCustom} />}
                              </>
                          );
                      })
                    : undefined}
            </div>
        </div>
    );

    return (
        <DraggableDialog
            dataTestId="email-actions-warning-dialog"
            draggable={false}
            title={t('previewTitle')}
            onSubmit={undefined}
            primaryBtnLabel={undefined}
            stage="FULLSCREEN"
            width={473}
            hideTitleCloseButton
            titleBarStyle="alternate"
            restoreButton={() => {
                return (
                    <div className="k-d-flex k-flex-row k-gap-1">
                        <Button
                            data-testid="preview-email-actions-cancel"
                            onClick={() => onClose('close')}
                            primary
                            disabled={disabledSendButton.value}
                        >
                            {tCommon('cancel')}
                        </Button>
                        <Button
                            fillMode="solid"
                            data-testid="preview-email-actions-send"
                            onClick={() => handleSubmit(onSubmit)()}
                            disabled={disabledSendButton.value || !isValid || !canEdit}
                            type="submit"
                        >
                            {tCommon('sendEmail')}
                        </Button>
                    </div>
                );
            }}
            setHeight="fit-content"
        >
            {body}
        </DraggableDialog>
    );
};

export default EmailActionsDialog;
